"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { urlFor } from "@/sanity/lib/image";

interface CustomCard {
  title: string;
  subtitle?: string;
  description?: string;
  image?: any;
  tags?: string[];
  link?: {
    url?: string;
    text?: string;
  };
}

interface CardsListProps {
  heading?: string;
  subheading?: string;
  contentType?: "projects" | "services" | "custom";
  projects?: any[];
  services?: any[];
  customCards?: CustomCard[];
  maxItems?: number;
  showTags?: boolean;
  showImages?: boolean;
  cardLayout?: "alternating" | "left" | "right";
  styling?: {
    backgroundColor?: string;
    padding?: "sm" | "md" | "lg" | "xl";
    maxWidth?: "sm" | "md" | "lg" | "xl" | "container" | "full";
    cardSpacing?: "tight" | "normal" | "loose";
  };
}

const ProjectCard = ({
  item,
  index,
  showTags = true,
  showImages = true,
  cardLayout = "alternating",
  contentType = "custom",
}: {
  item: any;
  index: number;
  showTags?: boolean;
  showImages?: boolean;
  cardLayout?: "alternating" | "left" | "right";
  contentType?: "projects" | "services" | "custom";
}) => {
  // Determine image position based on layout
  const getImagePosition = () => {
    if (cardLayout === "left") return "left";
    if (cardLayout === "right") return "right";
    return index % 2 === 0 ? "left" : "right";
  };

  const imagePosition = getImagePosition();
  const isImageLeft = imagePosition === "left";

  // Extract data based on content type
  let title, subtitle, description, image, tags, linkUrl, linkText;

  if (contentType === "custom") {
    title = item.title;
    subtitle = item.subtitle;
    description = item.description;
    image = item.image;
    tags = item.tags;
    linkUrl = item.link?.url;
    linkText = item.link?.text;
  } else if (contentType === "projects") {
    title = item.projectTitle;
    subtitle = item.client?.industry?.title || "WEBSITE DESIGN";
    description = item.projectOverview;
    image = item.featuredImage;
    
    // Handle services with safety checks for references
    const isReference = (obj: any): boolean => {
      return typeof obj === 'object' && obj && '_ref' in obj;
    };
    
    const serviceNames = item.services
      ?.filter((service: any) => service && !isReference(service))
      ?.map((service: any) => service.name)
      ?.filter((name: any): name is string => typeof name === 'string' && name.trim() !== '') || [];
    
    tags = serviceNames.length > 0 ? serviceNames : ["WEB DESIGN"];
    linkUrl = `/work/${item.slug}`;
    linkText = "View Case Study";
  } else if (contentType === "services") {
    title = item.title || item.name;
    subtitle = item.category?.title || "SERVICE";
    description = item.description;
    image = item.featuredImage || item.image;
    tags = item.serviceBadges || item.tags || [];
    linkUrl = `/services/${item.slug}`;
    linkText = "Learn More";
  } else {
    // Fallback for unknown content types
    title = item.title || item.name;
    subtitle = item.subtitle || item.industry || item.category;
    description = item.description || item.excerpt;
    image = item.image || item.featuredImage;
    tags = item.tags || item.services || item.technologies;
    linkUrl = item.link?.url || `/work/${item.slug}`;
    linkText = item.link?.text || "View Details";
  }

  return (
    <Link href={linkUrl || "#"} className="group block">
      <Card className="border border-border overflow-hidden rounded-2xl hover:shadow-xl transition-shadow duration-300">
        <CardContent
          className={cn(
            "p-0 flex flex-col md:flex-row",
            !isImageLeft && "md:flex-row-reverse"
          )}
        >
          {showImages && image && (
            <div className=" p-4 rounded-xl relative w-full md:w-2/5 h-64 md:h-auto min-h-[300px]">
              <Image
                src={urlFor(image).width(800).height(600).url()}
                alt={image.alt || title || "Card image"}
                fill
                sizes="(max-width: 768px) 100vw, 40vw"
                className="object-cover top-0"
              />
            </div>
          )}
          <div className={cn(
            "w-full p-6 md:p-10 flex flex-col justify-center",
            showImages ? "md:w-3/5" : "md:w-full"
          )}>
            {subtitle && (
              <p className="text-sm text-subheading uppercase tracking-wide mb-2">
                {subtitle}
              </p>
            )}
            <h3 className="text-2xl md:text-3xl font-medium text-heading mb-3">
              {title}
            </h3>
            {description && (
              <p className="text-text leading-relaxed mb-6">
                {description}
              </p>
            )}
            {showTags && tags && tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {tags.map((tag: any, tagIndex: number) => (
                  <Badge
                    key={typeof tag === 'string' ? tag : tagIndex}
                    variant="default"
                  >
                    {typeof tag === 'string' ? tag : tag.name || tag.title || `Tag ${tagIndex + 1}`}
                  </Badge>
                ))}
              </div>
            )}
            {/* <div className="flex items-center text-sm font-medium text-btn-text group-hover:text-text-muted transition-colors mt-auto">
              {linkText} <ArrowRight className="ml-1 w-4 h-4" />
            </div> */}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default function CardsList({
  heading,
  subheading,
  contentType = "custom",
  projects = [],
  services = [],
  customCards = [],
  maxItems,
  showTags = true,
  showImages = true,
  cardLayout = "alternating",
  styling = {},
}: CardsListProps) {
  // Determine which data to use based on content type
  let items: any[] = [];
  switch (contentType) {
    case "projects":
      items = projects;
      break;
    case "services":
      items = services;
      break;
    case "custom":
      items = customCards;
      break;
    default:
      items = customCards;
  }

  // Apply max items limit
  if (maxItems && maxItems > 0) {
    items = items.slice(0, maxItems);
  }

  // Check if there's any content to display
  if (!items || items.length === 0) {
    return null;
  }

  // Dynamic classes
  const bgClass = styling?.backgroundColor || "";
  const paddingClass = {
    sm: "py-8",
    md: "py-12", 
    lg: "py-16",
    xl: "py-20"
  }[styling?.padding || "lg"];
  
  const maxWidthClass = {
    container: "",
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg", 
    xl: "max-w-6xl",
    full: "max-w-full"
  }[styling?.maxWidth || "container"];

  const spacingClass = {
    tight: "space-y-8",
    normal: "space-y-16",
    loose: "space-y-24"
  }[styling?.cardSpacing || "normal"];

  return (
    <section className={`${bgClass} ${paddingClass}`}>
      <div className={`container mx-auto px-6 ${maxWidthClass}`}>
        {(heading || subheading) && (
          <div className="text-center mb-16">
            {subheading && (
              <p className="text-sm font-medium text-text-muted mb-2 uppercase tracking-wide">
                {subheading}
              </p>
            )}
            {heading && (
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text mb-6 leading-tight">
                {heading}
              </h2>
            )}
          </div>
        )}
        
        <div className={spacingClass}>
          {items.map((item, index) => (
            <ProjectCard
              key={item._key || item.id || index}
              item={item}
              index={index}
              showTags={showTags}
              showImages={showImages}
              cardLayout={cardLayout}
              contentType={contentType}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
